/*!
 * law-extract.js — what a visitor READS on a web page, for /signage-law-check.
 *
 * One extractor for three sources:
 *   1. a page our server fetched (SignageLawCheckHandler → thinned HTML →
 *      DOMParser in this browser: an inert document, nothing runs or loads);
 *   2. HTML the person pasted;
 *   3. the live page the person is looking at, through the bookmarklet — the
 *      only one that sees what scripts drew after load (a cookie banner, an app
 *      shell). That is why `extract` is written as ONE self-contained function:
 *      the bookmarklet carries its source text (Function#toString) into the
 *      other site, where nothing of ours is loaded.
 *
 * The anglicism extractor reads article prose and drops header, nav, footer,
 * forms and dialogs. A law check reads them on purpose: ст. 10.1 ЗоЗПП covers
 * every carrier of consumer information, and on a site that is the menu, the
 * buttons, the field hints, the image descriptions and the cookie notice.
 */
(function () {
    'use strict';

    /**
     * doc  — a Document (inert from DOMParser, or the live one)
     * opts — { url, live, maxItems }
     * → { title, lang, url, alternates:[{lang, href}], items:[{kind, text, n, hidden}], total, truncated }
     */
    function extract(doc, opts) {
        opts = opts || {};
        var MAX_ITEMS = opts.maxItems || 2500;
        var MAX_TEXT = 1200;
        var SKIP = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, SVG: 1, CANVAS: 1, IFRAME: 1, OBJECT: 1, HEAD: 1, CODE: 1, PRE: 1, KBD: 1, SAMP: 1 };
        // Interactive owners keep their own text (a button is one finding); block
        // owners collect the inline runs inside them (<p>Мы <b>открыты</b></p> is
        // one sentence, not three). Inline tags are never owners.
        var OWNERS = { BUTTON: 'button', A: 'link', LABEL: 'label', LEGEND: 'label', OPTION: 'label', SUMMARY: 'button',
            H1: 'heading', H2: 'heading', H3: 'heading', H4: 'heading', H5: 'heading', H6: 'heading',
            P: 'text', LI: 'text', TD: 'text', TH: 'label', DT: 'label', DD: 'text', FIGCAPTION: 'text', BLOCKQUOTE: 'text',
            CAPTION: 'label', DIV: 'text', SECTION: 'text', ARTICLE: 'text', ADDRESS: 'text', MAIN: 'text', ASIDE: 'text',
            HEADER: 'text', FOOTER: 'text', NAV: 'text', FORM: 'text', DIALOG: 'text', BODY: 'text', TR: 'text', UL: 'text', OL: 'text' };
        var COOKIE_RE = /(^|[\s_-])(cookie|cookies|consent|gdpr|cc-banner|cc_banner|cookiebar|cookie-notice|cmp)([\s_-]|$)/i;
        var norm = function (s) { return String(s == null ? '' : s).replace(/[\s \u200B]+/g, ' ').trim(); };
        var items = [];
        var seen = {};
        var total = 0;
        var truncated = false;

        function push(kind, text, hidden) {
            text = norm(text);
            if (!text || !/[A-Za-zА-Яа-яЁё]/.test(text)) return;
            if (text.length > MAX_TEXT) text = text.slice(0, MAX_TEXT);
            total++;
            var key = kind + '|' + text;
            if (seen[key]) { seen[key].n++; return; }
            if (items.length >= MAX_ITEMS) { truncated = true; return; }
            var it = { kind: kind, text: text, n: 1 };
            if (hidden) it.hidden = true;
            seen[key] = it;
            items.push(it);
        }

        function classList(el) {
            return ((el.getAttribute && (el.getAttribute('class') || '')) + ' ' + (el.id || '')).toLowerCase();
        }
        function isHidden(el) {
            for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
                if (n.hasAttribute('hidden') || n.getAttribute('aria-hidden') === 'true') return true;
                if (opts.live && n === el && typeof n.getClientRects === 'function' && n.getClientRects().length === 0) return true;
            }
            return false;
        }
        // The region a text belongs to decides its kind before its own tag does:
        // a link in the footer is «подвал», a button in the cookie notice is the notice.
        function region(el) {
            for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
                var tag = n.tagName;
                var cl = classList(n);
                if (COOKIE_RE.test(cl) || (n.getAttribute && /cookie/i.test(n.getAttribute('aria-label') || ''))) return 'cookie';
                if (tag === 'NAV' || n.getAttribute('role') === 'navigation') return 'nav';
                if (tag === 'HEADER' || n.getAttribute('role') === 'banner') return 'header';
                if (tag === 'FOOTER' || n.getAttribute('role') === 'contentinfo') return 'footer';
                if (tag === 'FORM') return 'form';
                if (n.getAttribute('role') === 'dialog' || tag === 'DIALOG') return 'dialog';
            }
            return 'main';
        }
        function ownerOf(node) {
            for (var n = node.parentElement; n; n = n.parentElement) {
                if (SKIP[n.tagName] || SKIP[String(n.tagName).toUpperCase()]) return null;
                if (n.getAttribute && n.getAttribute('role') === 'button') return n;
                if (OWNERS[n.tagName]) return n;
            }
            return null;
        }

        var root = doc.body || doc.documentElement;
        if (!root) return { title: '', lang: '', url: opts.url || '', alternates: [], items: [], total: 0, truncated: false };

        // 1) title + meta description — what a search result and a tab show
        try { push('title', doc.title || ''); } catch (e) {}
        try {
            var md = doc.querySelector('meta[name="description" i], meta[name="Description"]');
            if (md) push('meta', md.getAttribute('content') || '');
        } catch (e) {}

        // 2) text nodes grouped by their owning element
        var texts = [];
        var walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */, null);
        var owners = [];
        var buf = new Map();
        var node;
        while ((node = walker.nextNode())) {
            var v = node.nodeValue;
            if (!v || !/\S/.test(v)) continue;
            var ow = ownerOf(node);
            if (!ow) continue;
            if (!buf.has(ow)) { buf.set(ow, []); owners.push(ow); }
            buf.get(ow).push(v);
        }
        for (var i = 0; i < owners.length; i++) {
            var el = owners[i];
            var reg = region(el);
            var own = el.getAttribute && el.getAttribute('role') === 'button' ? 'button' : (OWNERS[el.tagName] || 'text');
            var kind = reg === 'cookie' ? 'cookie' : (reg === 'nav' || reg === 'header') ? (own === 'heading' ? 'heading' : 'nav')
                : reg === 'footer' ? 'footer' : own === 'link' ? 'link' : own;
            texts.push([kind, buf.get(el).join(' '), isHidden(el)]);
        }
        for (var t = 0; t < texts.length; t++) push(texts[t][0], texts[t][1], texts[t][2]);

        // 3) attribute texts a person sees or hears
        var attrs = [
            ['[placeholder]', 'placeholder', 'placeholder'],
            ['img[alt]', 'alt', 'alt'],
            ['input[type="submit"][value], input[type="button"][value], input[type="reset"][value]', 'value', 'button'],
            ['[aria-label]', 'aria-label', 'aria'],
            ['[title]:not(title)', 'title', 'tooltip'],
            ['[data-slc-svg-title]', null, 'aria'],
        ];
        for (var a = 0; a < attrs.length; a++) {
            var list;
            try { list = root.querySelectorAll(attrs[a][0]); } catch (e) { continue; }
            for (var j = 0; j < list.length; j++) {
                var e2 = list[j];
                var skip = false;
                for (var p = e2; p; p = p.parentElement) { if (SKIP[p.tagName]) { skip = true; break; } }
                if (skip) continue;
                var val = attrs[a][1] ? e2.getAttribute(attrs[a][1]) : e2.textContent;
                var k2 = region(e2) === 'cookie' ? 'cookie' : attrs[a][2];
                push(k2, val, isHidden(e2));
            }
        }

        var alternates = [];
        try {
            var links = doc.querySelectorAll('link[rel~="alternate"][hreflang]');
            for (var q = 0; q < links.length; q++) {
                var href = links[q].getAttribute('href') || '';
                try { href = new URL(href, opts.url || (doc.location && doc.location.href) || undefined).href; } catch (e) {}
                alternates.push({ lang: links[q].getAttribute('hreflang') || '', href: href });
            }
        } catch (e) {}

        var lang = '';
        try { lang = (doc.documentElement.getAttribute('lang') || '').trim(); } catch (e) {}
        return { title: norm(doc.title || ''), lang: lang, url: opts.url || '', alternates: alternates, items: items, total: total, truncated: truncated };
    }

    /** HTML string → extraction (inert document). */
    function fromHtml(html, url) {
        var doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
        return extract(doc, { url: url || '' });
    }

    /**
     * The bookmarklet: run `extract` on the page in front of the person and open
     * the tool with the result in the URL FRAGMENT (never sent to any server).
     * Our page answers with Cross-Origin-Opener-Policy, which cuts window.opener
     * for a popup opened by another site — so postMessage back is impossible and
     * the fragment is the channel. Compressed when the browser can.
     */
    function bookmarklet(toolUrl) {
        var body = '(function(){var X=' + extract.toString() + ';'
            + 'var r=X(document,{url:location.href,live:true,maxItems:1500});'
            + 'var j=JSON.stringify(r);var T=' + JSON.stringify(String(toolUrl)) + ';'
            + 'var go=function(h){var u=T+"#"+h;var w=window.open(u,"_blank");if(!w)location.href=u;};'
            + 'var b64=function(bytes){var s="";for(var i=0;i<bytes.length;i+=8192)s+=String.fromCharCode.apply(null,bytes.subarray(i,i+8192));return btoa(s).replace(/\\+/g,"-").replace(/\\//g,"_").replace(/=+$/,"");};'
            + 'var raw=new TextEncoder().encode(j);'
            + 'if(window.CompressionStream){new Response(new Blob([raw]).stream().pipeThrough(new CompressionStream("deflate-raw"))).arrayBuffer().then(function(ab){go("slcz="+b64(new Uint8Array(ab)));},function(){go("slc="+b64(raw));});}'
            + 'else go("slc="+b64(raw));})();';
        return 'javascript:' + encodeURIComponent(body).replace(/%20/g, ' ');
    }

    /** Fragment → extraction object (or null). Accepts both encodings. */
    function readFragment(hash) {
        var m = /^#?(slcz|slc)=([A-Za-z0-9_-]+)$/.exec(String(hash || ''));
        if (!m) return Promise.resolve(null);
        var bin = atob(m[2].replace(/-/g, '+').replace(/_/g, '/'));
        var bytes = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        var parse = function (u8) {
            try {
                var o = JSON.parse(new TextDecoder().decode(u8));
                return o && Array.isArray(o.items) ? o : null;
            } catch (e) { return null; }
        };
        if (m[1] === 'slc') return Promise.resolve(parse(bytes));
        if (!window.DecompressionStream) return Promise.resolve(null);
        return new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw')))
            .arrayBuffer().then(function (ab) { return parse(new Uint8Array(ab)); }, function () { return null; });
    }

    var api = { extract: extract, fromHtml: fromHtml, bookmarklet: bookmarklet, readFragment: readFragment };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (typeof window !== 'undefined') window.SignageLawExtract = api;
})();
